import { useState } from "react";
import { useSettings } from "./useSettings";
import { toast } from "sonner";

export default function SettingsInvoicing() {
  const { s, setS, save } = useSettings();
  const [stripeKey, setStripeKey] = useState("");
  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8">
      <section id="set-reminders" className="card-flat space-y-3 p-6" data-testid="reminder-settings">
        <p className="label-caps">Overdue invoice reminders</p>
        <p className="text-sm text-muted-foreground">Automatically email owners a payment reminder after an invoice goes unpaid, then repeat on a cycle.</p>
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Send automatic reminders</span>
          <button data-testid="reminder-toggle" onClick={() => save({ reminder_enabled: !(s.reminder_enabled ?? true) })}
            className={`h-8 w-14 rounded-full transition-colors ${(s.reminder_enabled ?? true) ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${(s.reminder_enabled ?? true) ? "translate-x-7" : ""}`} />
          </button>
        </label>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>Payment terms — invoices due within</span>
          <input data-testid="payment-terms-days" type="number" min="0" className="field-input w-20" value={s.payment_terms_days ?? 14} onFocus={(e) => e.target.select()}
            onChange={(e) => setS({ ...s, payment_terms_days: Number(e.target.value) })} />
          <span>days (0 = due on receipt)</span>
          <button data-testid="payment-terms-save" onClick={() => save({ payment_terms_days: Number(s.payment_terms_days ?? 14) })}
            className="h-12 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground">Save</button>
        </div>
        <label className="flex min-h-12 items-center gap-3 text-sm font-semibold">
          <input data-testid="auto-send-deposit-toggle" type="checkbox" className="h-6 w-6 accent-[hsl(158,68%,24%)]" checked={s.auto_send_deposit_invoice ?? true} onChange={(e) => save({ auto_send_deposit_invoice: e.target.checked })} />
          Auto-send deposit invoice when a quote is approved (off = save as draft and notify me)
        </label>
        {(s.reminder_enabled ?? true) && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>First reminder after</span>
              <input data-testid="reminder-days" type="number" min="1" className="field-input w-20" value={s.reminder_after_days} onFocus={(e) => e.target.select()}
                onChange={(e) => setS({ ...s, reminder_after_days: Number(e.target.value) })} />
              <span>days, then every</span>
              <input data-testid="reminder-freq" type="number" min="1" className="field-input w-20" value={s.reminder_frequency_days} onFocus={(e) => e.target.select()}
                onChange={(e) => setS({ ...s, reminder_frequency_days: Number(e.target.value) })} />
              <span>days</span>
              <button data-testid="reminder-save" onClick={() => save({ reminder_after_days: Number(s.reminder_after_days), reminder_frequency_days: Number(s.reminder_frequency_days), reminder_message: s.reminder_message ?? "" })}
                className="h-12 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground">Save</button>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>Stop after</span>
              <input data-testid="reminder-max" type="number" min="1" className="field-input w-20" value={s.reminder_max_count ?? 3} onFocus={(e) => e.target.select()}
                onChange={(e) => setS({ ...s, reminder_max_count: Number(e.target.value) })} />
              <span>reminders — then flag it for you to chase personally</span>
              <button data-testid="reminder-max-save" onClick={() => save({ reminder_max_count: Number(s.reminder_max_count ?? 3) })}
                className="h-12 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground">Save</button>
            </div>
            <textarea data-testid="reminder-message" className="field-input w-full" rows={2} maxLength={300}
              placeholder="Custom message for reminder emails (optional — e.g. Please e-transfer within 48 hours to keep your slot)"
              value={s.reminder_message ?? ""} onChange={(e) => setS({ ...s, reminder_message: e.target.value })} />
          </>
        )}
      </section>

      <section id="set-etransfer" className="card-flat space-y-3 p-6" data-testid="etransfer-settings">
        <p className="label-caps">Interac e-Transfer</p>
        <p className="text-sm text-muted-foreground">Owners send e-Transfers to this email with the invoice number as reference.</p>
        <div className="flex items-center gap-2">
          <input data-testid="etransfer-email" type="email" className="field-input flex-1" value={s.etransfer_email}
            onChange={(e) => setS({ ...s, etransfer_email: e.target.value })} />
          <button data-testid="etransfer-save" onClick={() => save({ etransfer_email: s.etransfer_email })} className="h-12 rounded-md bg-primary px-5 text-sm font-bold text-primary-foreground">Save</button>
        </div>
      </section>

      <section id="set-stripe" className="card-flat space-y-3 p-6" data-testid="stripe-settings">
        <p className="label-caps">Card payments (your Stripe)</p>
        <p className="text-sm text-muted-foreground">
          Connect your own Stripe account so your customers' card payments go straight to your bank. Without one, card payment stays disabled for your customers — e-Transfer keeps working.
        </p>
        {s.stripe_connected ? (
          <div className="flex items-center justify-between rounded-md bg-primary/10 p-4">
            <span className="text-sm font-bold text-primary" data-testid="stripe-connected">Connected · {s.stripe_key_masked}</span>
            <button data-testid="stripe-disconnect" onClick={() => save({ stripe_secret_key: "" })} className="h-10 rounded-md border border-destructive px-3 text-xs font-bold text-destructive">Disconnect</button>
          </div>
        ) : (
          <div className="space-y-3">
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground" data-testid="stripe-instructions">
              <li>Create or sign in to your Stripe account at <span className="font-semibold text-foreground">dashboard.stripe.com</span> — use your operating business name.</li>
              <li>Go to <span className="font-semibold text-foreground">Developers → API keys</span>.</li>
              <li>Copy your <span className="font-semibold text-foreground">Secret key</span> (starts with sk_test_ in test mode, sk_live_ when you're live).</li>
              <li>Paste it below and tap Connect — we verify it with Stripe before saving.</li>
            </ol>
            <div className="flex items-center gap-2">
              <input data-testid="stripe-key" type="password" className="field-input flex-1" placeholder="sk_live_… or rk_live_…" value={stripeKey}
                onChange={(e) => setStripeKey(e.target.value)} />
              <button data-testid="stripe-save" onClick={() => { save({ stripe_secret_key: stripeKey }); setStripeKey(""); }} className="h-12 rounded-md bg-primary px-5 text-sm font-bold text-primary-foreground">Connect</button>
            </div>
            <p className="text-xs text-muted-foreground">Until you connect, your customers see card payment disabled and pay by e-Transfer instead. Test card: 4242 4242 4242 4242, any future expiry, any CVC.</p>
          </div>
        )}
      </section>

      <section id="set-receipts" className="card-flat space-y-3 p-6" data-testid="reimbursement-settings">
        <p className="label-caps">Reimbursements</p>
        <p className="text-sm text-muted-foreground">
          Operators and crew can attach out-of-pocket costs (e.g. transfer-station drop-off fees) to a job — they land on the invoice as a reimbursement line, with the receipt photo shown to the owner.
        </p>
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Require a receipt photo for reimbursements</span>
          <button data-testid="toggle-receipt-required" onClick={() => save({ receipt_required: !s.receipt_required })}
            className={`h-8 w-14 rounded-full transition-colors ${s.receipt_required ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${s.receipt_required ? "translate-x-7" : ""}`} />
          </button>
        </label>
      </section>

      {s.offers_snow !== false && (
      <section id="set-snow-billing" className="card-flat space-y-3 p-6" data-testid="snow-billing-settings">
        <p className="label-caps">Snow billing</p>
        <p className="text-sm text-muted-foreground">Which billing models snow agreements can use, and the default for new snow services. Snow visits are dispatched per snowfall (snow events from the Today board), never on a schedule.</p>
        {[["per_visit", "Per visit"], ["monthly", "Monthly (flat fee / month)"], ["seasonal", "Seasonal package"]].map(([k, label]) => {
          const offered = s.snow_billing_offered || ["per_visit", "monthly", "seasonal"];
          const on = offered.includes(k);
          return (
            <label key={k} className="flex items-center justify-between rounded-md border border-border p-4">
              <span className="text-sm font-semibold">{label}</span>
              <button data-testid={`snow-billing-offered-${k}`} onClick={() => {
                const next = on ? offered.filter((x) => x !== k) : [...offered, k];
                if (!next.length) return toast.error("At least one snow billing model must stay on");
                const patch = { snow_billing_offered: next };
                if (!next.includes(s.snow_billing_default || "per_visit")) patch.snow_billing_default = next[0];
                save(patch);
              }} className={`h-8 w-14 shrink-0 rounded-full transition-colors ${on ? "bg-primary" : "bg-muted"}`}>
                <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${on ? "translate-x-7" : ""}`} />
              </button>
            </label>
          );
        })}
        <p className="text-xs font-bold text-muted-foreground">Default for new snow services</p>
        <div className="flex gap-2">
          {(s.snow_billing_offered || ["per_visit", "monthly", "seasonal"]).map((m) => (
            <button key={m} data-testid={`snow-billing-default-${m}`} onClick={() => save({ snow_billing_default: m })}
              className={`h-10 flex-1 rounded-md border text-xs font-bold ${(s.snow_billing_default || "per_visit") === m ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
              {{ per_visit: "Per visit", monthly: "Monthly", seasonal: "Seasonal" }[m]}</button>
          ))}
        </div>
      </section>
      )}
    </div>
  );
}
